import { useEffect, useState } from "react";
import { adminApi, errMsg } from "../api";
import { Field, TextInput, TextArea, UploadButton } from "../components";
import { toast } from "../../components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicationsAdmin() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/admin/publications").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
    adminApi.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const savePublication = async () => {
    if (!draft.title || !draft.media_id) return toast.error("Title and an uploaded file are required");
    setBusy(true);
    try {
      if (draft.id) {
        const { id, ...payload } = draft;
        await adminApi.put(`/admin/publications/${id}`, payload);
      } else {
        await adminApi.post("/admin/publications", draft);
      }
      toast.success("Publication saved — live on the public site");
      setDraft(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await adminApi.delete(`/admin/publications/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setProfile = async (mediaId) => {
    try {
      await adminApi.put("/admin/settings", { company_profile_media_id: mediaId });
      toast.success(mediaId ? "Company profile file replaced" : "Reverted to auto-generated profile");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-publications-page">
      <div className="adm-head">
        <div><h1>Publications & Downloads</h1><div className="sub">Uploadable documents + the Company Profile slot</div></div>
        <button className="adm-btn primary" onClick={() => setDraft({ title: "", description: "", media_id: "", filename: "" })} data-testid="add-publication">+ New publication</button>
      </div>

      <div className="adm-card" data-testid="company-profile-slot" style={{ borderLeft: "4px solid var(--crimson)" }}>
        <h2>Company Profile download slot</h2>
        <p style={{ fontSize: ".86rem", color: "var(--muted)", marginBottom: 12 }}>
          The public "Download Company Profile" button serves {settings?.company_profile_media_id
            ? "the uploaded file below." : "an auto-generated PDF built from site content."}
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <UploadButton accept=".pdf" kind="company-profile" label="Upload replacement PDF" testid="upload-profile"
            onUploaded={(m) => setProfile(m.id)} />
          {settings?.company_profile_media_id && (
            <>
              <a className="adm-btn ghost" href={`${BACKEND_URL}/api/company-profile`} target="_blank" rel="noreferrer">Preview current</a>
              <button className="adm-btn danger" onClick={() => setProfile(null)} data-testid="reset-profile">Revert to auto-generated</button>
            </>
          )}
        </div>
      </div>

      {draft && (
        <div className="adm-card" style={{ borderColor: "var(--crimson)" }} data-testid="publication-editor">
          <h2>{draft.id ? "Edit publication" : "New publication"}</h2>
          <Field label="Title"><TextInput value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} data-testid="pub-title" /></Field>
          <Field label="Description"><TextArea value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} rows={2} data-testid="pub-description" /></Field>
          <Field label="File">
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {draft.filename && <span className="mono">{draft.filename}</span>}
              <UploadButton accept=".pdf,.doc,.docx" kind="publication" testid="upload-publication"
                onUploaded={(m) => setDraft({ ...draft, media_id: m.id, filename: m.filename })} />
            </div>
          </Field>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="adm-btn ghost" onClick={() => setDraft(null)}>Cancel</button>
            <button className="adm-btn primary" onClick={savePublication} disabled={busy} data-testid="save-publication">{busy ? "Saving…" : "Save publication"}</button>
          </div>
        </div>
      )}

      <div className="adm-card" style={{ padding: 0, overflow: "auto" }}>
        <table className="adm-table">
          <thead><tr><th>Title</th><th>File</th><th>Added</th><th style={{ width: 160 }}></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{p.title}</td>
                <td className="mono">{p.filename}</td>
                <td className="mono">{(p.created_at || "").slice(0, 10)}</td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="adm-btn ghost sm" onClick={() => setDraft(p)}>Edit</button>
                    <button className="adm-btn danger sm" onClick={() => remove(p)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="adm-empty">No publications uploaded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
